/* eslint-disable jsx-a11y/alt-text */
import { StructuredText } from 'react-datocms'
import styled from 'styled-components'
import { WhitepaperArticleRecord } from 'types'

import { HiddenAnchor } from '@/blocks/HiddenAnchor'
import { ImageBlock } from '@/blocks/Image'
import { VideoBlock } from '@/blocks/Video'

import { LinkListBlock } from '@/components/blocks/LinkListBlock'
import { MultipleAuthorsSelect } from '@/components/blocks/MultipleAuthorsSelect'
import { Sources } from '@/components/blocks/Sources'
import { Container } from '@/components/shared/Container'

const StyledArticleContent = styled.div`
  position: relative;

  > h1,
  > h2,
  > h3,
  > h4,
  > h5,
  > h6,
  > blockquote,
  > ul,
  > ol,
  > p {
    max-width: 684px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 24px;
    padding-right: 24px;
  }
  strong {
    font-family: ${(props) => props.theme.v2.font.lotaGrotesqueSemiBold};
  }
  p {
    font-family: ${(props) => props.theme.v2.font.lotaGrotesqueLight};
  }
  ul,
  ol {
    padding-left: 64px;
    margin-top: 12px;
    margin-bottom: 12px;
  }
  blockquote {
    padding-left: 52px;
    position: relative;
    margin-top: 24px;
    margin-bottom: 24px;
    p {
      font-style: italic;
      font-size: 24px;
      line-height: 38px;
    }
    &:before {
      content: '';
      position: absolute;
      width: 2px;
      height: 100%;
      top: 0;
      left: 24px;
      border-radius: 2px;
      background-color: ${(props) => props.theme.v2.interactive.bright};
    }
    footer {
      font-size: 20px;
      line-height: 34px;
      font-family: ${(props) => props.theme.v2.font.lotaGrotesqueLight};
      &:before {
        content: '- ';
      }
    }
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    blockquote {
      p {
        font-size: 18px;
        line-height: 32px;
      }
      footer {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }
`

const StyledLinkListBlock = styled.div`
  > ul {
    padding-left: 0;
    margin: 0;

    li {
      a {
        padding-top: 20px;
        padding-bottom: 20px;
        color: ${(props) => props.theme.v2.interactive.bright};

        &:hover {
          color: ${(props) => props.theme.v2.interactive.subdued};
        }
      }

      &:first-of-type {
        a {
          padding-top: 10px;
        }
      }

      &:not(:last-child) {
        border-bottom: 1px solid ${(props) => props.theme.v2.onSurface.border};
      }

      span {
        background-image: url(/assets/img/icons/link-arrow-blue.svg);
        width: 20px;
      }
    }
  }
`

const renderBlock = ({ record }) => {
  switch (record.__typename) {
    case 'HiddenAnchorRecord':
      return <HiddenAnchor record={record} />
    case 'ImageRecord':
      return <ImageBlock record={record} />
    case 'VideoRecord':
      return <VideoBlock record={record} />
    case 'LinkListRecord':
      return (
        <Container $size="narrow">
          <StyledLinkListBlock>
            <LinkListBlock record={record} />
          </StyledLinkListBlock>
        </Container>
      )
    case 'MultipleAuthorsSelectRecord':
      return <MultipleAuthorsSelect record={record} />
    case 'SourceRecord':
      return <Sources record={record} expanded={true} />
    default:
      return null
  }
}

export const WhitepaperArticleContent = ({ article }: { article: WhitepaperArticleRecord }) => {
  return (
    <StyledArticleContent className="articleContent">
      <StructuredText data={article.articleContent} renderBlock={renderBlock} />
    </StyledArticleContent>
  )
}
